import { useState } from 'react';
import { useQuery } from 'react-query';

import { BulkClUpdateResponse } from '../abstractions/BuldClUpdate.interface';
import { DEFAULT_ERROR_MESSAGE } from '@/config/text.const';
import { useEffectExceptOnMount } from '@/hooks/useEffectExceptOnMount.hook';
import { BULK_CL_UPDATE_FILES_PER_PAGE } from '@/config/common.config';
import { bulkClUpdateClient } from '@/services/http/clients/bulkClUpdateClient';
import { notificationService } from '@/services/notification/notification.client';

type PageConfig = {
  readonly pageIndex: number;
  readonly pageSize: number;
};

export function useGetUploadedFiles(onSuccess?: (data: BulkClUpdateResponse) => void) {
  const [pageConfig, setPageConfig] = useState<PageConfig>({
    pageIndex: 1,
    pageSize: BULK_CL_UPDATE_FILES_PER_PAGE
  });

  const loadData = async () => {
    const requestUrl = '/GetPaginatedDocumentsFunction';
    const response = await bulkClUpdateClient.get<BulkClUpdateResponse>(requestUrl, {
      responseType: 'json',
      params: {
        pageIndex: pageConfig.pageIndex,
        pageSize: pageConfig.pageSize
      },
    });

    return response.data;
  };

  const { refetch, isFetching, data } = useQuery<BulkClUpdateResponse>({
    queryKey: 'uploaded-bulk-cl-update-files',
    queryFn: loadData,
    enabled: false,
    retry: false,
    onError: (err: any) => notificationService.open({
      title: 'Error',
      text: err?.message ?? DEFAULT_ERROR_MESSAGE,
      type: 'danger'
    }),
    onSuccess: (response) => onSuccess?.(response),
  });

  const getUploadedFiles = (pageIndex: number, pageSize: number) => {
    setPageConfig({ pageIndex, pageSize });
  };

  useEffectExceptOnMount(() => {
    refetch();
  }, [pageConfig]);

  return {
    getUploadedFiles,
    isLoading: isFetching,
    files: data?.Items ?? [],
    totalPages: data?.TotalPages ?? 0,
  } as const;
}
